import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { addYears } from 'date-fns';
import type { ServiceOrder, UpdateServiceOrderPayload } from 'contracts/service-orders';
import { Skeleton } from 'components/ui/skeleton';
import { Box } from 'components/ui/Box';
import { Form, FormField } from 'components/ui/form';
import { InputGs, DatePickerGs, DateTimePickerGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';
import { cn } from 'lib/utils';

import { formSchema, type FormSchema } from './form-schema';
import { createUpdateServiceOrderPayload } from './utils';

export type UpdateServiceOrderFormRef = {
  getFormData: () => UpdateServiceOrderPayload
}

type Props = Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> & {
  serviceOrder: ServiceOrder,
  onSubmit: (data: UpdateServiceOrderPayload) => void,
  loading: boolean,
  enableDataEditing?: boolean,
}

const FormComponent =
  React.forwardRef<UpdateServiceOrderFormRef, Props>(({
    serviceOrder,
    onSubmit,
    loading,
    enableDataEditing,
    className,
    ...props
  }, ref) => {
    const productsDeliveredAtDisabled = React.useMemo(() => {
      return !!serviceOrder?.productsDeliveredAt;
    }, [serviceOrder?.productsDeliveredAt]);

    const [startDateDisabled, setStartDateDisabled] = React.useState(() => {
      return !!serviceOrder?.startDate || !serviceOrder?.productsDeliveredAt;
    });

    const [endDateDisabled, setEndDateDisabled] = React.useState(() => {
      return !!serviceOrder?.endDate || !serviceOrder?.startDate;
    });

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        startDate: undefined,
        endDate: undefined,
        deadlineDate: undefined,
        workedHours: undefined,
        personQuantity: undefined,
        productsDeliveredAt: undefined,
      },
    });

    const hydrateForm = React.useCallback((serviceOrder: ServiceOrder) => {
      const {
        productsDeliveredAt,
        startDate,
        endDate,
        deadlineDate,
        workedHours,
        personQuantity,
      } = serviceOrder;

      if (productsDeliveredAt) {
        form.setValue('productsDeliveredAt', new Date(productsDeliveredAt));
      }

      if (startDate) {
        form.setValue('startDate', new Date(startDate));
      }

      if (endDate) {
        form.setValue('endDate', new Date(endDate));
      }

      if (deadlineDate) {
        form.setValue('deadlineDate', new Date(deadlineDate));
      }

      if (workedHours) {
        form.setValue('workedHours', workedHours);
      }

      if (personQuantity) {
        form.setValue('personQuantity', personQuantity);
      }
    }, [form]);

    const onFormSubmit = React.useCallback((formData: FormSchema) => {
      const payload = createUpdateServiceOrderPayload(formData);
      onSubmit(payload);
    }, [onSubmit]);

    React.useEffect(() => {
      hydrateForm(serviceOrder);
    }, [serviceOrder, hydrateForm]);

    React.useImperativeHandle(ref, () => {
      return {
        getFormData: () => {
          const formData = form.getValues();
          return createUpdateServiceOrderPayload(formData);
        },
      };
    }, [form]);

    return (
      <Box {...props} className={cn('relative', className)}>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onFormSubmit)}
            className={'flex flex-col gap-4'}
          >
            <fieldset
              disabled={!enableDataEditing}
              className={cn(
                'grid grid-cols-1 lg:grid-cols-3 gap-4',
                !enableDataEditing && '[&_input]:bg-gray-100 [&_button]:bg-gray-100',
              )}
            >
              <FormField
                control={form.control}
                name={'deadlineDate'}
                render={({ field }) => (
                  <DatePickerGs
                    {...field}
                    label={'Prazo'}
                    fieldValue={field.value ? new Date(field.value) : undefined}
                    fieldOnChange={field.onChange}
                    maxDate={addYears(new Date(), 100)}
                    disabled={!enableDataEditing}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'workedHours'}
                render={({ field }) => (
                  <InputGs
                    {...field}
                    label={'Horas trabalhadas'}
                    type="number"
                    onChange={field.onChange}
                    disabled={!enableDataEditing}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'personQuantity'}
                render={({ field }) => (
                  <InputGs
                    {...field}
                    label={'Quantidade de pessoas'}
                    type="number"
                    onChange={field.onChange}
                    disabled={!enableDataEditing}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'productsDeliveredAt'}
                render={({ field }) => (
                  <DateTimePickerGs
                    {...field}
                    label={'Data de recebimento dos produtos'}
                    maxDate={new Date()}
                    fieldValue={field.value ? new Date(field.value) : undefined}
                    disabled={productsDeliveredAtDisabled || !enableDataEditing}
                    fieldOnChange={(value) => {
                      setStartDateDisabled(!value);
                      if (!value) form.setValue('startDate', undefined);
                      if (!value) form.setValue('endDate', undefined);
                      field.onChange(value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'startDate'}
                render={({ field }) => (
                  <DateTimePickerGs
                    {...field}
                    label={'Data de início'}
                    fieldValue={field.value ? new Date(field.value) : undefined}
                    disabled={startDateDisabled || !enableDataEditing}
                    minDate={new Date(serviceOrder.createdAt)}
                    maxDate={new Date()}
                    fieldOnChange={(value) => {
                      setEndDateDisabled(!value);
                      if (!value) form.setValue('endDate', undefined);
                      field.onChange(value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'endDate'}
                render={({ field }) => (
                  <DateTimePickerGs
                    {...field}
                    label={'Data de término'}
                    maxDate={new Date()}
                    fieldValue={field.value ? new Date(field.value) : undefined}
                    disabled={endDateDisabled || !enableDataEditing}
                    fieldOnChange={field.onChange}
                  />
                )}
              />
            </fieldset>
            <div>
              <Button.Root type={'submit'} size={'sm'} disabled={loading || !enableDataEditing}>
                {loading ? <ActivityIndicator /> : 'Salvar'}
              </Button.Root>
            </div>
          </form>
        </Form>
      </Box>
    );
  });
FormComponent.displayName = 'UpdateServiceOrderForm';

const MySkeleton = () => {
  return (
    <Box className={'relative'}>
      <div className={'grid grid-cols-1 lg:grid-cols-3 gap-4'}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={'flex flex-col gap-2'}>
            <Skeleton className="w-20 h-5" />
            <Skeleton className="w-full h-10" />
          </div>
        ))}
      </div>
      <Skeleton className="w-40 h-10 mt-6" />
    </Box>
  );
};

export const UpdateServiceOrderForm = Object.assign(FormComponent, {
  Skeleton: MySkeleton,
});
